/**
 * CommandBar — palette de commandes globale (style VSCode/Notion).
 *
 * @remarks
 * - Trigger : barre cliquable au centre du Header (style discret).
 * - Modal : ouvre via Cmd/Ctrl+K ou clic sur le trigger. Le raccourci est
 *   déclaré dans `shell-manifest` ; ici on écoute la commande qu'il émet.
 * - Navigation clavier : ↑↓ pour bouger, ⏎ pour sélectionner, Esc pour fermer.
 * - Filtrage : fuzzy sur title/description/category.
 *
 * Migré du legacy `components/molecules/CommandBar/` (CSS Module) vers
 * Tailwind 4. Les commandes utilisent `useNavigate` pour les transitions
 * et `useAuthStore` pour les actions d'auth — ce ne sont plus des stubs
 * `console.log` comme en V1.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Settings, Folder, LogOut, ShieldAlert } from 'lucide-react';
import { useCommand } from '@/app/commands/command-bus';
import { useAuthStore } from '@/app/stores/use-auth-store';
import { performLogout } from '@/lib/auth/logout';
import { formatShortcutKeys, isMacPlatform } from '@/lib/utils/format-shortcut';
import { ADMIN_PATHS } from '@/features/admin/router/paths';
import { CommandBarModal, type CommandItem } from './CommandBarModal';
import { Icon } from '@/shared/ui';

const CATEGORIES = {
  NAVIGATION: 'Navigation',
  ACTIONS: 'Actions',
  SETTINGS: 'Paramètres',
} as const;

export function CommandBar() {
  const navigate = useNavigate();
  const hasRole = useAuthStore((s) => s.hasRole);

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchQuery('');
    setSelectedIndex(0);
  }, []);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setSearchQuery('');
    setSelectedIndex(0);
  }, []);

  // Commandes — rebuilt at each render to capture latest navigate/logout closures.
  const commands = useMemo<CommandItem[]>(() => {
    const list: CommandItem[] = [
      {
        id: 'go-tests',
        title: 'Aller aux tests',
        description: 'Accéder aux tests techniques',
        category: CATEGORIES.NAVIGATION,
        icon: <Icon icon={FileText} size="lg" />,
        action: () => {
          void navigate('/tests');
          handleClose();
        },
      },
      {
        id: 'go-results',
        title: 'Aller à mes résultats',
        description: 'Accéder aux résultats des tests',
        category: CATEGORIES.NAVIGATION,
        icon: <Icon icon={Folder} size="lg" />,
        action: () => {
          void navigate('/user-tests');
          handleClose();
        },
      },
      {
        id: 'open-settings',
        title: 'Ouvrir les paramètres',
        description: "Ouvrir les paramètres de l'application",
        category: CATEGORIES.SETTINGS,
        icon: <Icon icon={Settings} size="lg" />,
        // Même chaîne que `settings:open` dans le manifest settings.
        shortcut: formatShortcutKeys('mod+,'),
        action: () => {
          void navigate('/settings');
          handleClose();
        },
      },
      {
        id: 'logout',
        title: 'Se déconnecter',
        description: 'Se déconnecter du compte',
        category: CATEGORIES.ACTIONS,
        icon: <Icon icon={LogOut} size="lg" />,
        action: () => {
          handleClose();
          // logout serveur (révocation + clear cookie) puis clear local, et
          // enfin redirection — le store est déjà vidé quand on navigue.
          void performLogout().finally(() => {
            void navigate('/login');
          });
        },
      },
    ];

    if (hasRole('admin')) {
      list.push({
        id: 'go-admin',
        title: "Aller à l'administration",
        description: "Accéder à l'administration des évaluations",
        category: CATEGORIES.NAVIGATION,
        icon: <Icon icon={ShieldAlert} size="lg" />,
        action: () => {
          void navigate(ADMIN_PATHS.evaluations.list);
          handleClose();
        },
      });
    }

    return list;
  }, [navigate, hasRole, handleClose]);

  const filteredCommands = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return commands.filter(
      (c) =>
        c.title.toLowerCase().includes(query) ||
        (c.description?.toLowerCase().includes(query) ?? false) ||
        c.category.toLowerCase().includes(query),
    );
  }, [commands, searchQuery]);

  const groupedCommands = useMemo(() => {
    return filteredCommands.reduce<Record<string, CommandItem[]>>((acc, c) => {
      const list = acc[c.category] ?? [];
      list.push(c);
      acc[c.category] = list;
      return acc;
    }, {});
  }, [filteredCommands]);

  const handleSelectCommand = useCallback((command: CommandItem) => {
    command.action();
  }, []);

  // Cmd/Ctrl+K → ouvre, ou ferme si déjà ouverte
  const handleToggle = useCallback(() => {
    if (isOpen) handleClose();
    else handleOpen();
  }, [isOpen, handleOpen, handleClose]);
  useCommand('shell:toggle-command-palette', handleToggle);

  // Auto-focus input when modal opens
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  // Keyboard nav inside modal
  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filteredCommands.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const cmd = filteredCommands[selectedIndex];
        if (cmd) handleSelectCommand(cmd);
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, selectedIndex, filteredCommands, handleClose, handleSelectCommand]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  const modifierKey = isMacPlatform() ? '⌘' : 'Ctrl';

  return (
    <>
      {/* Trigger button (visible dans le header) */}
      <button
        type="button"
        onClick={handleOpen}
        data-tour="header-command-bar"
        aria-label="Ouvrir la palette de commandes"
        className="text-fg-muted border-border bg-bg-muted hover:bg-bg-active mx-auto flex h-[1.875rem] w-full max-w-[600px] items-center gap-2 rounded-md border px-2.5 text-sm transition-all"
      >
        <Search className="size-3.5 shrink-0" aria-hidden />
        <span className="flex-1 text-left text-xs">Rechercher ou saisir une commande…</span>
        <kbd className="text-fg-muted border-border bg-bg hidden h-5 shrink-0 items-center gap-1 rounded-sm border px-1.5 text-xs leading-none font-medium opacity-70 md:inline-flex">
          <span>{modifierKey}</span>
          <span>K</span>
        </kbd>
      </button>

      {isOpen && (
        <CommandBarModal
          onClose={handleClose}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          inputRef={inputRef}
          filteredCommands={filteredCommands}
          groupedCommands={groupedCommands}
          selectedIndex={selectedIndex}
          onSelectCommand={handleSelectCommand}
          onHoverCommand={setSelectedIndex}
        />
      )}
    </>
  );
}
